/**
 * LanguageSwitcher.tsx
 *
 * Navbar & settings language switcher with native labels, instant RTL layout mirroring,
 * full useTranslation reactivity, and non-intrusive locality auto-detect confirmation.
 */

import React, { useState, useEffect, useRef } from 'react';
import { globalLanguageRegistryService, type LanguageDefinition } from '../../i18n/language-registry-service.js';
import { useTranslation } from '../../i18n/useTranslation.js';
import { normalizeBcp47Locale } from '../../i18n/locale-detector.js';

export const LanguageSwitcher: React.FC = () => {
  const { locale, setLocale, direction, isRtl } = useTranslation();
  const i18n = globalLanguageRegistryService;
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [autoDetectNotice, setAutoDetectNotice] = useState<string | null>(null);

  const containerRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const activeLanguages = i18n.getActiveLanguages();

  // Outside click and Escape key dismissal for popover menu
  useEffect(() => {
    if (!isOpen) return;

    const handlePointerDown = (e: MouseEvent | TouchEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setIsOpen(false);
        triggerRef.current?.focus();
      }
    };

    document.addEventListener('pointerdown', handlePointerDown);
    window.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('pointerdown', handlePointerDown);
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen]);

  useEffect(() => {
    // Check locality detection on initial mount
    const detected = i18n.detectLocalityPreference();
    const normalizedDetected = normalizeBcp47Locale(detected);
    if (normalizedDetected !== 'en' && normalizedDetected !== locale) {
      const match = activeLanguages.find(
        (l) => l.code === detected || normalizeBcp47Locale(l.code) === normalizedDetected
      );
      if (match) {
        setAutoDetectNotice(match.name);
      }
    }
  }, [locale]);

  const handleSelectLanguage = (langCode: string) => {
    const updated = i18n.setUserLanguage(langCode);
    setLocale(langCode);
    setIsOpen(false);
    setAutoDetectNotice(null);
    triggerRef.current?.focus();
  };

  const currentDef =
    activeLanguages.find(
      (l) =>
        l.code === locale ||
        l.defaultLocale === locale ||
        normalizeBcp47Locale(l.code) === locale ||
        normalizeBcp47Locale(l.defaultLocale) === locale
    ) || activeLanguages[0];

  const currentDirection = currentDef?.direction || direction;

  return (
    <div ref={containerRef} style={{ position: 'relative', display: 'inline-block' }}>
      {/* Trigger Button — Compact 2-Letter Badge + Flag */}
      <button
        ref={triggerRef}
        type="button"
        onClick={() => setIsOpen((prev) => !prev)}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        aria-label="Change language"
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: '5px',
          padding: '4px 8px',
          backgroundColor: 'var(--theme-bg-card, #151d2a)',
          border: '1px solid var(--theme-border, #1e293b)',
          borderRadius: 'var(--theme-radius-md, 6px)',
          color: 'var(--theme-text-primary, #f8fafc)',
          fontSize: '11px',
          fontWeight: 700,
          cursor: 'pointer',
          minHeight: 'var(--a11y-target-min, 24px)',
        }}
      >
        <span>{currentDef.flagEmoji || '🌐'}</span>
        <span>{currentDef.shortCode || currentDef.code.toUpperCase()}</span>
        {currentDirection === 'rtl' && (
          <span style={{ fontSize: '9px', color: 'var(--theme-accent, #38bdf8)' }}>RTL</span>
        )}
        <span style={{ fontSize: '9px', color: 'var(--theme-text-muted, #94a3b8)' }}>▾</span>
      </button>

      {/* Language Selection Menu */}
      {isOpen && (
        <div
          role="listbox"
          aria-label="Select language"
          style={{
            position: 'absolute',
            top: 'calc(100% + 6px)',
            right: currentDirection === 'rtl' ? 'auto' : 0,
            left: currentDirection === 'rtl' ? 0 : 'auto',
            minWidth: '180px',
            backgroundColor: 'var(--theme-bg-modal, #0f172a)',
            border: '1px solid var(--theme-border-highlight, #38bdf8)',
            borderRadius: 'var(--theme-radius-lg, 12px)',
            boxShadow: 'var(--theme-shadow-lg, 0 10px 25px rgba(0,0,0,0.5))',
            zIndex: 50,
            padding: '6px',
            display: 'flex',
            flexDirection: 'column',
            gap: '2px',
          }}
        >
          {activeLanguages.map((lang) => {
            const isSelected =
              lang.code === locale ||
              lang.defaultLocale === locale ||
              normalizeBcp47Locale(lang.code) === locale ||
              normalizeBcp47Locale(lang.defaultLocale) === locale;

            return (
              <div
                key={lang.code}
                role="option"
                aria-selected={isSelected}
                onClick={() => handleSelectLanguage(lang.code)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  padding: '8px 12px',
                  borderRadius: '6px',
                  backgroundColor: isSelected ? 'var(--theme-bg-card-hover, #1e293b)' : 'transparent',
                  color: isSelected ? 'var(--theme-accent, #38bdf8)' : 'var(--theme-text-secondary, #cbd5e1)',
                  fontSize: '12px',
                  fontWeight: isSelected ? 600 : 400,
                  cursor: 'pointer',
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <span>{lang.flagEmoji || '🌐'}</span>
                  <span>{lang.name}</span>
                </div>
                {lang.direction === 'rtl' && (
                  <span style={{ fontSize: '10px', opacity: 0.7 }}>RTL</span>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Non-Intrusive Locality Auto-Detect Toast Confirmation */}
      {autoDetectNotice && (
        <div
          style={{
            position: 'fixed',
            bottom: '60px',
            right: '24px',
            padding: '12px 16px',
            backgroundColor: 'var(--theme-bg-modal, #0f172a)',
            border: '1px solid var(--theme-border-highlight, #38bdf8)',
            borderRadius: 'var(--theme-radius-lg, 12px)',
            boxShadow: 'var(--theme-shadow-lg)',
            zIndex: 100,
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            fontSize: '12px',
          }}
        >
          <span>📍 We detected your language as <strong>{autoDetectNotice}</strong>.</span>
          <button
            onClick={() => handleSelectLanguage(i18n.detectLocalityPreference())}
            style={{
              padding: '4px 8px',
              backgroundColor: 'var(--theme-accent, #38bdf8)',
              color: 'var(--theme-text-inverse, #0b0f19)',
              border: 'none',
              borderRadius: '4px',
              fontWeight: 600,
              cursor: 'pointer',
            }}
          >
            Switch
          </button>
          <button
            onClick={() => setAutoDetectNotice(null)}
            style={{
              background: 'none',
              border: 'none',
              color: 'var(--theme-text-muted, #94a3b8)',
              cursor: 'pointer',
              fontSize: '14px',
            }}
          >
            ✕
          </button>
        </div>
      )}
    </div>
  );
};
